import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  Calculator, Scale, Target, TrendingUp, Check, Crown, ArrowRight, Sparkles,
} from "lucide-react";
import { fmtUSD } from "@/lib/format";

const tools = [
  { icon: Calculator, title: "Shift Pay Calculator", desc: "See exactly what a shift earns — base, overtime, differentials and bonuses." },
  { icon: Target, title: "Income Goal Planner", desc: "Find out how many extra shifts it takes to hit your income goal." },
  { icon: Scale, title: "Job Offer Comparison", desc: "Compare two offers side-by-side on pay, benefits, commute and more." },
  { icon: TrendingUp, title: "Career Growth Tools", desc: "Map your next move and grow your nursing income over time." },
];

const freeFeatures = ["Basic shift calculator", "Monthly income calculator", "1 job comparison per month", "Basic dashboard"];
const proFeatures = [
  "Unlimited calculations", "Unlimited job comparisons", "Save calculations",
  "Income goal planning", "Career roadmap tools", "Advanced earnings comparisons", "Future premium resources",
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <header className="sticky top-0 z-30 border-b border-slate-100 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Logo />
          <div className="flex items-center gap-2">
            <Button variant="ghost" data-testid="nav-login-btn" onClick={() => navigate("/login")} className="text-[#0F2747]">
              Log in
            </Button>
            <Button data-testid="nav-signup-btn" onClick={() => navigate("/signup")} className="rounded-full bg-[#18A6A6] hover:bg-[#148f8f]">
              Start Free
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-24">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#EAF4FA] px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#18A6A6]">
              <Sparkles className="h-3.5 w-3.5" /> For nurses who want more
            </span>
            <h1 className="mt-5 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-[#0F2747] sm:text-5xl lg:text-6xl">
              Make smarter nursing career and income decisions.
            </h1>
            <p className="mt-5 max-w-xl text-lg text-slate-600">
              Calculate your shift pay, compare job offers, set income goals, and see what it could
              take to reach your next financial milestone.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-start-free-btn" onClick={() => navigate("/signup")}
                className="rounded-full bg-[#18A6A6] px-7 py-6 text-base hover:bg-[#148f8f]">
                Start Free <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button variant="outline" data-testid="hero-how-it-works-btn"
                onClick={() => document.getElementById("tools")?.scrollIntoView({ behavior: "smooth" })}
                className="rounded-full border-slate-300 px-7 py-6 text-base text-[#0F2747]">
                See How It Works
              </Button>
            </div>
          </div>

          {/* Mock dashboard */}
          <div className="relative">
            <div className="absolute -right-6 -top-6 h-40 w-40 rounded-full bg-[#EAF4FA] blur-2xl" />
            <div className="relative rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl">
              <p className="text-xs font-bold uppercase tracking-widest text-[#18A6A6]">Current Estimated Income</p>
              <p className="mt-1 font-heading text-4xl font-extrabold text-[#0F2747]">{fmtUSD(108108)}</p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-[#F8FAFC] p-4">
                  <p className="text-xs text-slate-500">Income Goal</p>
                  <p className="font-heading text-xl font-bold text-[#0F2747]">{fmtUSD(150000)}</p>
                </div>
                <div className="rounded-2xl bg-[#F8FAFC] p-4">
                  <p className="text-xs text-slate-500">Income Gap</p>
                  <p className="font-heading text-xl font-bold text-[#D6A84B]">{fmtUSD(41892)}</p>
                </div>
              </div>
              <div className="mt-5">
                <div className="flex justify-between text-xs font-medium text-slate-500">
                  <span>72% of your $150K goal</span><span>72%</span>
                </div>
                <div className="mt-2 h-3 rounded-full bg-slate-100 p-0.5">
                  <div className="h-full w-[72%] rounded-full bg-gradient-to-r from-[#18A6A6] to-[#20C997]" />
                </div>
              </div>
              <div className="mt-5 flex items-center gap-3 rounded-2xl bg-[#0F2747] p-4 text-white">
                <Calculator className="h-5 w-5 text-[#18A6A6]" />
                <div>
                  <p className="text-xs text-slate-300">Estimated Shift Pay</p>
                  <p className="font-heading text-2xl font-bold">{fmtUSD(1042)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Built for nurses */}
      <section id="tools" className="bg-[#F8FAFC] py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="max-w-2xl font-heading text-3xl font-bold text-[#0F2747] sm:text-4xl">
            Built for nurses who want more from their career.
          </h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {tools.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm transition-all hover:shadow-md">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EAF4FA] text-[#18A6A6]">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-4 font-heading text-lg font-semibold text-[#0F2747]">{title}</h3>
                <p className="mt-1.5 text-sm text-slate-600">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="font-heading text-3xl font-bold text-[#0F2747] sm:text-4xl">Simple, honest pricing</h2>
            <p className="mt-3 text-slate-600">Start free. Upgrade when you're ready to level up.</p>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {/* Free */}
            <div className="rounded-3xl border border-slate-200 bg-white p-8">
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">Free</span>
              <p className="mt-4 font-heading text-4xl font-extrabold text-[#0F2747]">$0</p>
              <ul className="mt-6 space-y-3">
                {freeFeatures.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm text-slate-700">
                    <Check className="h-4 w-4 text-[#18A6A6]" /> {f}
                  </li>
                ))}
              </ul>
              <Button variant="outline" data-testid="pricing-free-btn" onClick={() => navigate("/signup")}
                className="mt-8 w-full rounded-full border-slate-300 py-6 text-[#0F2747]">
                Start Free
              </Button>
            </div>
            {/* Pro */}
            <div className="relative overflow-hidden rounded-3xl border border-[#D6A84B]/40 bg-gradient-to-br from-[#0F2747] via-[#16355F] to-[#0F2747] p-8 text-white shadow-xl">
              <div className="flex items-center gap-2 text-[#D6A84B]">
                <Crown className="h-4 w-4" />
                <span className="text-xs font-bold uppercase tracking-widest">RN Level Up Pro</span>
              </div>
              <div className="mt-4 flex items-end gap-2">
                <p className="font-heading text-4xl font-extrabold">$9.99</p>
                <span className="pb-1 text-slate-300">/month</span>
              </div>
              <p className="text-sm text-[#D6A84B]">or $79/year · Save 34%</p>
              <ul className="mt-6 space-y-3">
                {proFeatures.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm text-slate-100">
                    <Check className="h-4 w-4 text-[#D6A84B]" /> {f}
                  </li>
                ))}
              </ul>
              <Button data-testid="pricing-pro-btn" onClick={() => navigate("/signup")}
                className="mt-8 w-full rounded-full bg-[#D6A84B] py-6 font-semibold text-[#0F2747] hover:bg-[#c69a3f]">
                Start RN Level Up Pro
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100 bg-[#F8FAFC] py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Logo />
          <p className="mt-4 max-w-3xl text-xs leading-relaxed text-slate-500">
            RN Level Up provides educational estimates and career planning tools. Results are not
            financial, employment, tax, legal, or clinical advice. Actual compensation varies by
            employer, location, taxes, benefits, overtime rules, and individual circumstances.
          </p>
          <p className="mt-3 max-w-3xl text-xs leading-relaxed text-slate-500">
            RN Level Up is a career and financial planning tool for nurses and is not intended for
            storing patient information or protected health information.
          </p>
          <p className="mt-4 text-xs text-slate-400">© {new Date().getFullYear()} RN Level Up. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
